import React from 'react';
import { styled } from '@mui/material';
import { Close } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Colors } from 'library';

interface ChipProps {
    children: React.ReactNode;
    variant?: 'outlined' | 'filled';
    bgColor?: string;
    borderColor?: string;
    textColor?: string;
    size?: 'small' | 'medium';
    padding?: 'normal' | 'expanded';
    removable?: boolean;
    onRemove?: (e: React.MouseEvent) => void;
    to?: string;
    dashed?: boolean;
}

export function Chip({
    children,
    variant = 'outlined',
    bgColor,
    borderColor,
    textColor,
    size = 'medium',
    padding = 'normal',
    removable,
    onRemove,
    to,
    dashed = false,
}: ChipProps) {
    const content = (
        <>
            {children}
            {removable && (
                <RemoveButton onClick={onRemove} type="button">
                    <Close sx={{ fontSize: size === 'small' ? 12 : 14 }} />
                </RemoveButton>
            )}
        </>
    );
    if (to) {
        return (
            <ChipLink
                to={to}
                variant={variant}
                bgColor={bgColor}
                borderColor={borderColor}
                textColor={textColor}
                size={size}
                padding={padding}
                dashed={dashed}
            >
                {content}
            </ChipLink>
        );
    }
    return (
        <StyledChip
            variant={variant}
            bgColor={bgColor}
            borderColor={borderColor}
            textColor={textColor}
            size={size}
            padding={padding}
            dashed={dashed}
        >
            {content}
        </StyledChip>
    );
}

type ChipStyleProps = {
    variant: 'outlined' | 'filled';
    bgColor?: string;
    borderColor?: string;
    textColor?: string;
    size?: 'small' | 'medium';
    padding?: 'normal' | 'expanded';
    dashed?: boolean;
};

const chipStyles = ({ variant, bgColor, borderColor, textColor, size, padding, dashed }: ChipStyleProps) => ({
    display: 'inline-flex' as const,
    alignItems: 'center' as const,
    gap: 6,
    padding: padding === 'expanded' ? '7px 10px' : size === 'small' ? '1px 6px' : '3px 7px',
    borderRadius: 16,
    fontSize: size === 'small' ? 11 : 13,
    fontWeight: 500,
    whiteSpace: 'nowrap' as const,
    flexShrink: 0,
    textDecoration: 'none' as const,
    ...(variant === 'outlined' && {
        color: textColor ?? Colors.textSecondary,
        backgroundColor: bgColor ?? Colors.white,
        border: `1px ${dashed ? 'dashed' : 'solid'} ${borderColor ?? Colors.border}`,
    }),
    ...(variant === 'filled' && {
        color: textColor ?? Colors.white,
        backgroundColor: bgColor ?? Colors.primary,
        border: borderColor ? `1px ${dashed ? 'dashed' : 'solid'} ${borderColor}` : 'none',
    }),
});

const StyledChip = styled('span')<ChipStyleProps>(chipStyles);

const ChipLink = styled(Link)<ChipStyleProps>(chipStyles, ({ variant, textColor }) => {
    const baseColor = textColor ?? (variant === 'outlined' ? Colors.textSecondary : Colors.white);
    return {
        cursor: 'pointer',
        verticalAlign: 'middle',
        '&, &:link, &:visited, &:focus, &:active': { color: baseColor },
        '&:hover': variant === 'filled' ? { color: baseColor, opacity: 0.85 } : { color: Colors.primary },
    };
});

const RemoveButton = styled('button')({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 16,
    height: 16,
    marginLeft: 2,
    padding: 0,
    border: 'none',
    borderRadius: '50%',
    backgroundColor: 'transparent',
    color: 'inherit',
    cursor: 'pointer',
    opacity: 0.7,
    '&:hover': {
        opacity: 1,
        backgroundColor: 'rgba(0,0,0,0.08)',
    },
});
